import { forwardRef, useState } from 'react';
import { cn } from '@/utils/cn';
import { Eye, EyeOff } from 'lucide-react';
import type { InputProps } from './Input.types';
import styles from './Input.module.scss';

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      size = 'md',
      hasError = false,
      fullWidth = false,
      className,
      disabled,
      readOnly,
      ...props
    },
    ref
  ) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const isPasswordType = type === 'password';

    const inputClasses = cn(
      styles.input,
      styles[`input--${size}`],
      hasError && styles['input--error'],
      disabled && styles['input--disabled'],
      readOnly && styles['input--readonly'],
      fullWidth && styles['input--full-width'],
      isPasswordType && styles['input--with-suffix'],
      className
    );

    const inputElement = (
      <input
        ref={ref}
        type={isPasswordType && isPasswordVisible ? 'text' : type}
        className={inputClasses}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={hasError}
        {...props}
      />
    );

    if (isPasswordType) {
      return (
        <div className={styles.inputWrapper}>
          {inputElement}
          <button
            type="button"
            className={styles.inputToggle}
            // Evita que el input pierda el foco al pulsar el icono: sin esto el
            // blur dispara la validación (react-hook-form) y el re-render se
            // come el click, así que el toggle "no funcionaba" con el campo
            // enfocado.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setIsPasswordVisible((prev) => !prev)}
            disabled={disabled}
            tabIndex={-1}
            aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
            title={isPasswordVisible ? 'Hide password' : 'Show password'}
          >
            {isPasswordVisible ? (
              <EyeOff size={20} aria-hidden />
            ) : (
              <Eye size={20} aria-hidden />
            )}
          </button>
        </div>
      );
    }

    return inputElement;
  }
);

Input.displayName = 'Input';

export default Input;
